/**
 * @file Main route definitions.
 */
import loadable from '@loadable/component';

import RequireAuth from '../components/routing/RequireAuth';
import EntryForward from '../components/routing/EntryForward';
import ErrorView from '../components/global/ErrorView';
import NotFound from '../apps/pro/views/NotFound';
import authRoutes from './routes-auth';
import resortsRoutes from './routes-resorts';
import purchaseRoutes from './routes-purchase';
import joinRoutes from './routes-join';
import discoverRoutes from './routes-discover';
import viewRoutes from './routes-view';
import slugRoutes from './routes-slugs';
import rndRoutes from './routes-rnd';
import redirectRoutes from './routes-redirects';
import StandalonePhotoViewerContainer from '../containers/view/StandalonePhotoViewerContainer';
import ExternalAlbumUploadView from '../apps/pro/views/ExternalAlbumUpload';
import { AUTH_TYPE } from '../constants/auth';
import LoadableFullScreenLoader from '../components/global/loadable/LoadableFullScreenLoader';
import { basenamePattern } from '../util/routes';
import AllPhotosAccessLinkHandler from '../apps/pro/views/AllPhotosAccessLinkHandler';
import AutoContributorLinkHandler from '../apps/pro/views/AutoContributorLinkHandler';
import UserPageWrapper from '../apps/web-gallery/views/UserPage';
import AccountMarketingMaterialPrintTemplate from '../apps/pro/views/AlbumFolder/AlbumFolderMarketing/components/AccountMarketingMaterialPrintTemplate/AccountMarketingMaterialPrintTemplate';
import printServiceRoutes from '../services/prints/routes';
import albumCustomPrintServiceRoutes from '../services/album-custom-prints/routes';
import FileCommentItemRedirect from '../components/global/FileComments/components/FileCommentItemRedirect';
import AccountFileReactionRedirect from '../components/global/FileReactionList/components/AccountFileReactionRedirect';
import SlideshowView from '../apps/pro/views/Album/Slideshow';
import SlideshowDesignerView from '../apps/pro/views/Album/Slideshow/SlideshowDesignerView';
import SlideshowSolicitationView from '../apps/pro/views/Album/Slideshow/SlideshowSolicitationView';
import SlideshowGreetingView from '../apps/pro/views/Album/Slideshow/SlideshowGreetingView';
import StandaloneWeddingsBusinessCard from '../apps/pro/views/StandaloneWeddingsBusinessCard';
import ManageSubscriptions from '../components/global/ManageSubscriptions';
import PublicMarketingMaterialConfigurator from '../apps/pro/components/ContributorShare/components/MarketingMaterialConfigurator/PublicMarketingMaterialConfigurator/PublicMarketingMaterialConfigurator';
import AlbumSlideshowIntroLayoutDesignerView from '../apps/pro/views/Album/AlbumSlideshow/AlbumSlideshowIntroLayoutDesignerView/AlbumSlideshowIntroLayoutDesignerView';
import ConsumerFeedbackView from '../apps/consumer/views/feedback/ConsumerFeedbackView';
import BridgeAPI from '../apps/pro/services/bridge/BridgeAPI';
import SpecialtyEventUploadView from '../views/contributors/SpecialtyEventUploadView';
import HierarchyItemEventView from '../views/hierarchy-items/HierarchyItemEventView';
import SpecialtyEventJoinView from '../views/contributors/SpecialtyEventJoinView';
import StandaloneSpecialtyEventUploadView from '../views/contributors/StandaloneSpecialtyEventUploadView';
import SpecialtyEventGiftView from '../views/contributors/SpecialtyEventGiftView';
import SignupAlbumCreate from '../apps/shutterbug/views/SignupAlbumCreate';
import SignupAlbumSelect from '../apps/shutterbug/views/SignupAlbumSelect';
import RequireAutoContributorAuth from '../components/routing/RequireAutoContributorAuth';
import { ALBUM_TYPE } from '../constants/albums';
import FreemiumEventTutorialStandaloneWizard from '../apps/pro/views/AlbumFolder/FreemiumEventTutorialWizard/FremiumEventTutorialStandaloneWizard';
import MarketingAccountMatchesView from '../views/linked-identities/MarketingAccountMatchesView';
import ThankYouView from '../components/global/ThankYouView';
import AccountDeleteView from '../components/global/AccountDeleteView';

const ProductMasterRouteLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "ProductMasterRoute" */ './master-routes/ProductMasterRoute'
    ),
  { fallback: LoadableFullScreenLoader },
);

const ContributorsMasterRouteLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "ContributorsMasterRoute" */ './master-routes/ContributorsMasterRoute'
    ),
  { fallback: LoadableFullScreenLoader },
);

const ConsumerMasterRouteLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "ConsumerMasterRoute" */ '../apps/consumer/routes/ConsumerMasterRoute'
    ),
  { fallback: LoadableFullScreenLoader },
);

const CommandMasterRouteLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "CommandMasterRoute" */ '../apps/xcomm/routes/CommandMasterRoute'
    ),
  { fallback: LoadableFullScreenLoader },
);

const SelfieStationMasterRouteLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "SelfieStationMasterRoute" */ '../apps/selfie-station/routes/SelfieStationMasterRoute'
    ),
  { fallback: LoadableFullScreenLoader },
);

const AdminMasterRouteLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "AdminMasterRoute" */ '../apps/admin/routes/AdminMasterRoute'
    ),
  { fallback: LoadableFullScreenLoader },
);

const ShutterbugMasterRouteLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "ShutterbugMasterRoute" */ '../apps/shutterbug/routes/ShutterbugMasterRoute'
    ),
  { fallback: LoadableFullScreenLoader },
);

const CardsMasterRouteLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "CardsMasterRouteLoadable" */ '../apps/cards/routes/CardsMasterRoute'
    ),
  { fallback: LoadableFullScreenLoader },
);

const routes = [
  {
    path: '/',
    exact: true,
    component: () => {
      // TODO remove this and properly handle "back to legacy web"
      if (window.location && window.location.reload) {
        window.location.reload();
      }

      return null;
    },
  },
  {
    path: '/app',
    exact: true,
    component: RequireAuth(EntryForward),
  },
  ...authRoutes,
  {
    path: `/app/${basenamePattern}`,
    exact: false,
    component: ProductMasterRouteLoadable,
  },
  ...resortsRoutes,
  ...purchaseRoutes,
  ...discoverRoutes,
  ...joinRoutes,
  ...viewRoutes,
  ...redirectRoutes,
  ...printServiceRoutes,
  ...albumCustomPrintServiceRoutes,
  {
    path: '/command',
    exact: false,
    component: CommandMasterRouteLoadable,
  },
  ...rndRoutes,
  {
    path: '/contributors',
    exact: false,
    component: ContributorsMasterRouteLoadable,
  },
  {
    path: '/consumer',
    exact: false,
    component: ConsumerMasterRouteLoadable,
  },
  {
    path: '/selfie-station',
    exact: false,
    component: SelfieStationMasterRouteLoadable,
  },
  {
    path: '/admin',
    exact: false,
    component: AdminMasterRouteLoadable,
  },
  {
    path: '/shutterbug',
    exact: false,
    component: ShutterbugMasterRouteLoadable,
  },
  {
    path: '/cards',
    exact: false,
    component: CardsMasterRouteLoadable,
  },
  {
    path: '/marketing-materials/print/:accountMarketingMaterialId',
    exact: true,
    component: AccountMarketingMaterialPrintTemplate,
  },
  {
    path: '/s/comments/:accountFileCommentId',
    component: FileCommentItemRedirect,
    exact: true,
  },
  {
    path: '/s/reactions/:accountFileReactionId',
    component: AccountFileReactionRedirect,
    exact: true,
  },
  {
    path: '/s/all-photos-access/:entityId',
    exact: true,
    component: RequireAuth(AllPhotosAccessLinkHandler, {
      redirectUrl: `/auth/phone`,
    }),
  },
  {
    path: '/s/:entityId',
    exact: true,
    component: RequireAutoContributorAuth(AutoContributorLinkHandler, {
      redirectUrl: `/auth/phone`,
      skippedAlbumTypes: [ALBUM_TYPE.EVENT],
    }),
  },
  {
    path: '/s/profile/:accountId',
    exact: true,
    component: StandaloneWeddingsBusinessCard,
  },
  {
    path: '/s/event-invite/:albumFolderId/gifts',
    exact: true,
    component: RequireAuth(SpecialtyEventGiftView),
  },
  {
    path: '/s/event-invite/:albumFolderId/join',
    exact: true,
    component: SpecialtyEventJoinView,
  },
  {
    path: '/s/event-invite/:albumFolderId',
    exact: true,
    component: SpecialtyEventUploadView,
  },
  {
    path: '/s/standalone-event-invite/:albumId',
    exact: true,
    component: StandaloneSpecialtyEventUploadView,
  },
  {
    path: '/s/event-view/:hierarchyItemId',
    exact: true,
    component: RequireAuth(HierarchyItemEventView),
  },
  {
    path: '/s/event-tutorial-config/:entityId',
    exact: true,
    component: RequireAuth(FreemiumEventTutorialStandaloneWizard),
  },
  {
    path: '/ext-upload/:albumId',
    exact: true,
    component: RequireAuth(ExternalAlbumUploadView, {
      authType: AUTH_TYPE.PHONE,
    }),
  },
  {
    path: '/p/:photoId',
    exact: true,
    component: StandalonePhotoViewerContainer,
  },
  {
    path: '/@:username',
    exact: true,
    component: UserPageWrapper,
  },
  {
    path: `/signup/albums/select`,
    exact: true,
    component: SignupAlbumSelect,
  },
  {
    path: `/signup/albums/create`,
    exact: true,
    component: SignupAlbumCreate,
  },
  {
    path: `/albums/:albumId/intro-layout-designer`,
    exact: true,
    component: AlbumSlideshowIntroLayoutDesignerView,
  },
  {
    path: `/albums/:albumId/slideshow/designer`,
    exact: false,
    component: SlideshowDesignerView,
  },
  {
    path: `/albums/:albumId/slideshow/greeting`,
    exact: true,
    component: SlideshowGreetingView,
  },
  {
    path: `/albums/:albumId/slideshow/solicitation`,
    exact: true,
    component: SlideshowSolicitationView,
  },
  {
    path: `/albums/:albumId/slideshow`,
    exact: true,
    component: SlideshowView,
  },
  {
    path: `/albums/:albumId/slideshow/s/:slideId`,
    exact: true,
    component: SlideshowView,
  },
  {
    path: `/marketing-materials/:albumFolderId`,
    exact: true,
    component: PublicMarketingMaterialConfigurator,
  },
  {
    path: `/feedback`,
    exact: true,
    component: ConsumerFeedbackView,
  },
  {
    path: '/manage-subscriptions',
    exact: true,
    component: RequireAuth(ManageSubscriptions),
  },
  {
    path: '/web-bridge',
    exact: true,
    component: RequireAuth(BridgeAPI),
  },
  {
    path: '/marketing-account-matches/redirect',
    exact: true,
    component: MarketingAccountMatchesView,
  },
  {
    path: '/thank-you',
    exact: true,
    component: ThankYouView,
  },
  {
    path: '/error',
    exact: true,
    component: ErrorView,
  },
  {
    path: '/account/delete',
    exact: true,
    component: RequireAuth(AccountDeleteView),
  },
  // Critical for slug routes to load just before not found
  ...slugRoutes,
  {
    component: NotFound,
  },
];

export default routes;
